import { useEffect, useRef, useState } from 'react';

// Sticky "has this element been scrolled into view yet" tracker, backed by IntersectionObserver.
// Used to lazy-load below-the-fold dashboard sections (Local Info: Traffic News + Weather) so
// their data/maps only fetch once a user actually scrolls to them, rather than on every dashboard
// load. `inView` flips true the first time the element enters the viewport (with `rootMargin` as
// a preload buffer) and then stays true — no need to re-observe once loaded. Mirrors the mobile
// app's useSeenOnce hook (mobile/src/hooks/useSeenOnce.ts), which does the same job without
// IntersectionObserver (React Native has no DOM/viewport API).
export function useInViewOnce<T extends Element>(rootMargin = '200px') {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView || !ref.current) return;
    const el = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return { ref, inView };
}
